<template>
  <div class="tw-relative tw-mb-[24px] tw-flex tw-flex-col">
    <div
      v-if="disabled"
      class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
    ></div>
    <div
      class="tw-my-[24px] tw-mb-[24px] tw-flex tw-items-center tw-justify-between"
    >
      <div class="tw-flex tw-flex-col">
        <span
          class="tw-text-[16px] tw-font-semibold tw-leading-none tw-text-primary-grey"
        >
          Price range
        </span>

        <span
          class="tw-mt-[4px] tw-text-[12px] tw-leading-none tw-text-primary-grey"
        >
          Per person per week
        </span>
      </div>

      <button
        type="button"
        class="tw-rounded-[4px] tw-p-1 tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-text-uhblue-500 tw-underline tw-decoration-uhblue-500 tw-decoration-solid hover:tw-bg-[#F3F4F6] focus-visible:tw-ring focus-visible:tw-ring-sky-200 focus-visible:tw-ring-opacity-50 disabled:tw-bg-white disabled:tw-text-gray-400 disabled:tw-decoration-gray-400"
        aria-label="Clear price range filters"
        :disabled="isClearDisabled"
        @click.prevent="clearValues"
      >
        Clear
      </button>
    </div>

    <div class="tw-flex">
      <div class="tw-w-full">
        <div class="tw-mb-1 tw-flex tw-items-center tw-gap-2">
          <label class="tw-mx-auto tw-text-[14px] tw-leading-[21px]"
            >Minimum</label
          >
        </div>

        <input
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          :value="formattedMinValue"
          :placeholder="minInputPlaceholder"
          data-testid="mobile-stacked-filters-min-price"
          :class="[
            'tw-flex tw-h-[50px] tw-w-full tw-flex-[1_0_0] tw-items-center tw-rounded-full tw-border tw-bg-white tw-px-[16px] tw-py-[8px] tw-text-center tw-text-[16px] tw-leading-[24px]',
            hasError
              ? errorClasses
              : minValue !== ''
              ? activeClasses
              : [
                  'tw-border-gray-300',
                  'hover:tw-border-uhblue-700',
                  'focus:tw-border-uhblue-700',
                ],
            isMinFocusVisible
              ? [
                  'tw-ring',
                  'tw-ring-offset-1',
                  'tw-ring-sky-200',
                  'tw-ring-opacity-50',
                ]
              : ['focus:tw-ring-0', 'focus-visible:tw-ring-0'],
          ]"
          @input="onMinInput"
          @keydown="enforceNumericInput"
          @focus="onMinFocus"
          @blur="onInputBlur"
        />
      </div>

      <div
        class="tw-mt-[25px] tw-flex tw-h-[50px] tw-content-center tw-items-center tw-justify-center tw-px-[16px] tw-font-semibold tw-leading-[24px]"
      >
        -
      </div>

      <div class="tw-w-full">
        <div class="tw-mb-1 tw-flex tw-items-center tw-gap-2">
          <label class="tw-mx-auto tw-text-[14px] tw-leading-[21px]"
            >Maximum</label
          >
        </div>

        <input
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          :value="formattedMaxValue"
          :placeholder="maxInputPlaceholder"
          data-testid="mobile-stacked-filters-max-price"
          :class="[
            'tw-flex tw-h-[50px] tw-w-full tw-flex-[1_0_0] tw-items-center tw-rounded-full tw-border tw-bg-white tw-px-[16px] tw-py-[8px] tw-text-center tw-text-[16px] tw-leading-[24px]',
            hasError
              ? errorClasses
              : maxValue !== ''
              ? activeClasses
              : [
                  'tw-border-gray-300',
                  'hover:tw-border-uhblue-700',
                  'focus:tw-border-uhblue-700',
                ],
            isMaxFocusVisible
              ? [
                  'tw-ring',
                  'tw-ring-offset-1',
                  'tw-ring-sky-200',
                  'tw-ring-opacity-50',
                ]
              : ['focus:tw-ring-0', 'focus-visible:tw-ring-0'],
          ]"
          @input="onMaxInput"
          @keydown="enforceNumericInput"
          @focus="onMaxFocus"
          @blur="onInputBlur"
        />
      </div>
    </div>

    <p
      v-if="hasError"
      class="tw-absolute tw-bottom-[-22px] tw-text-[12px] tw-text-red-500"
    >
      The max price cannot be less than the min price.
    </p>
  </div>
</template>

<script>
export default {
  name: 'StackedPriceFilter',
  props: {
    selectedMin: {
      type: [Number, String],
      default: null,
    },
    selectedMax: {
      type: [Number, String],
      default: null,
    },
    minPlaceholder: {
      type: [Number, String],
      default: null,
    },
    maxPlaceholder: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      minValue: '',
      maxValue: '',
      activeClasses: ['tw-border-2', 'tw-border-uhblue-500', 'tw-m-0'],
      errorClasses: [
        'tw-border-2',
        'tw-border-red-500',
        'hover:tw-border-red-700',
        'tw-m-0',
      ],
      hasError: false,
      validationTimeout: null,
      isMinFocusVisible: false,
      isMaxFocusVisible: false,
      inputModality: 'mouse',
    };
  },
  computed: {
    formattedMinValue() {
      return this.minValue !== '' ? `£${this.minValue}` : '';
    },
    formattedMaxValue() {
      return this.maxValue !== '' ? `£${this.maxValue}` : '';
    },
    minInputPlaceholder() {
      if (this.selectedMin !== null && this.selectedMin !== undefined) {
        return `£${this.selectedMin}`;
      }
      if (this.minPlaceholder !== null && this.minPlaceholder !== undefined) {
        return `£${this.minPlaceholder}`;
      }
      return '£-';
    },
    maxInputPlaceholder() {
      if (this.selectedMax !== null && this.selectedMax !== undefined) {
        return `£${this.selectedMax}`;
      }
      if (this.maxPlaceholder !== null && this.maxPlaceholder !== undefined) {
        return `£${this.maxPlaceholder}`;
      }
      return '£-';
    },
    isClearDisabled() {
      return this.minValue === '' && this.maxValue === '';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.setKeyboardModality);
    document.addEventListener('mousedown', this.setMouseModality);

    this.setDefaults();
  },
  unmounted() {
    document.removeEventListener('keydown', this.setKeyboardModality);
    document.removeEventListener('mousedown', this.setMouseModality);
  },
  methods: {
    onMinInput(event) {
      const raw = event.target.value.replace(/[^0-9]/g, '');
      this.minValue = raw;
      this.debounceValidate();
      this.emitChange();
    },
    onMaxInput(event) {
      const raw = event.target.value.replace(/[^0-9]/g, '');
      this.maxValue = raw;
      this.debounceValidate();
      this.emitChange();
    },
    clearValues() {
      this.minValue = '';
      this.maxValue = '';
      this.hasError = false;

      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
      }

      this.emitChange();
    },
    emitChange() {
      this.$emit('change', {
        min: this.minValue || null,
        max: this.maxValue || null,
        error: this.hasError,
      });
    },
    setDefaults() {
      if (this.selectedMin !== null) {
        this.minValue = this.selectedMin.toString();
      }

      if (this.selectedMax !== null) {
        this.maxValue = this.selectedMax.toString();
      }

      this.validateValues();
      this.emitChange();
    },
    enforceNumericInput(e) {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
      ];

      if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
    validateValues() {
      const min = parseInt(this.minValue, 10);
      const max = parseInt(this.maxValue, 10);

      this.hasError = !isNaN(min) && !isNaN(max) && max < min;
    },
    // Ensures that the error message doesn't display while a user is still typing a number.
    debounceValidate() {
      const min = parseInt(this.minValue, 10);
      const max = parseInt(this.maxValue, 10);

      const isError = !isNaN(min) && !isNaN(max) && max < min;

      if (!isError) {
        // If the values are valid clear the error immediately.
        if (this.validationTimeout) {
          clearTimeout(this.validationTimeout);
          this.validationTimeout = null;
        }

        this.hasError = false;
      } else {
        // If the values are invalid show error after 700ms of no user input.
        if (this.validationTimeout) {
          clearTimeout(this.validationTimeout);
        }

        this.validationTimeout = setTimeout(() => {
          this.hasError = true;
        }, 700);
      }
    },
    setKeyboardModality() {
      this.inputModality = 'keyboard';
    },
    setMouseModality() {
      this.inputModality = 'mouse';
    },
    onMinFocus() {
      this.isMinFocusVisible = this.inputModality === 'keyboard';
    },
    onMaxFocus() {
      this.isMaxFocusVisible = this.inputModality === 'keyboard';
    },
    onInputBlur() {
      this.isMinFocusVisible = false;
      this.isMaxFocusVisible = false;
    },
  },
};
</script>
